import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'react-qr-code';
import Layout from '../components/common/Layout';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import { rrhhInventarioService } from '../services/rrhhInventario';
import toast from 'react-hot-toast';
import { Inbox, Link2, Copy, QrCode, Plus, X, FileDown, PackageCheck } from 'lucide-react';

const TIPOS = {
    epp_uniforme: 'EPP / Uniforme',
    reposicion: 'Reposición',
    documento: 'Documento',
    otro: 'Otro',
};

const ESTADOS = {
    pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700' },
    en_proceso: { label: 'En proceso', cls: 'bg-blue-100 text-blue-700' },
    atendida: { label: 'Atendida', cls: 'bg-green-100 text-green-700' },
    rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
};

const MOTIVOS_ENTREGA = [
    { value: 'ingreso_nuevo', label: 'Ingreso nuevo' },
    { value: 'perdida', label: 'Pérdida' },
    { value: 'renovacion', label: 'Renovación' },
];

const campoCls = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';
const inputCls = `w-full ${campoCls}`;

const BadgeEstado = ({ estado }) => {
    const e = ESTADOS[estado] || { label: estado, cls: 'bg-gray-100 text-gray-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${e.cls}`}>{e.label}</span>;
};

// ─── Enlace público para compartir con los colaboradores ────────────────────

const EnlacePublico = () => {
    const [verQr, setVerQr] = useState(false);
    const enlace = `${window.location.origin}/solicitud-rrhh`;

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(enlace);
            toast.success('Enlace copiado');
        } catch {
            toast.error('No se pudo copiar, cópialo a mano');
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700 flex-1 min-w-0">
                    <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="flex-shrink-0">Formulario para colaboradores (sin login):</span>
                    <a href={enlace} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{enlace}</a>
                </div>
                <div className="flex gap-2">
                    <button onClick={copiar} className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copiar
                    </button>
                    <button onClick={() => setVerQr((v) => !v)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                        <QrCode className="h-3.5 w-3.5 mr-1" /> QR
                    </button>
                </div>
            </div>
            {verQr && (
                <div className="mt-4 flex flex-col items-center">
                    <div className="bg-white p-3 rounded-lg">
                        <QRCode value={enlace} size={160} />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Imprímelo y pégalo en el área de trabajo para que lo escaneen.</p>
                </div>
            )}
        </div>
    );
};

// ─── Modal: atender una solicitud ───────────────────────────────────────────

const ModalAtender = ({ solicitud, items, onCerrar, onActualizada }) => {
    const [respuesta, setRespuesta] = useState(solicitud.respuesta || '');
    const [lineas, setLineas] = useState(
        solicitud.detalles.length
            ? solicitud.detalles.map((d) => ({ itemId: d.item_id ? String(d.item_id) : '', cantidad: d.cantidad }))
            : [{ itemId: '', cantidad: 1 }]
    );
    const [modoEntrega, setModoEntrega] = useState(false);
    const [motivoEntrega, setMotivoEntrega] = useState('renovacion');
    const [guardando, setGuardando] = useState(false);
    const abierta = ['pendiente', 'en_proceso'].includes(solicitud.estado);

    const actualizarLinea = (idx, campo, valor) =>
        setLineas((prev) => prev.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));

    const cambiarEstado = async (estado) => {
        if (estado === 'rechazada' && !respuesta.trim()) { toast.error('Escribe el motivo del rechazo para el colaborador'); return; }
        setGuardando(true);
        try {
            const actualizada = await rrhhInventarioService.cambiarEstadoSolicitud(solicitud.id, { estado, respuesta: respuesta || null });
            toast.success(`Solicitud ${ESTADOS[estado].label.toLowerCase()}`);
            onActualizada(actualizada);
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'No se pudo actualizar la solicitud');
        } finally {
            setGuardando(false);
        }
    };

    const entregar = async () => {
        if (lineas.some((l) => !l.itemId)) { toast.error('Selecciona el producto de cada línea'); return; }
        if (lineas.some((l) => !l.cantidad || Number(l.cantidad) <= 0)) { toast.error('La cantidad debe ser mayor a 0'); return; }
        setGuardando(true);
        try {
            const actualizada = await rrhhInventarioService.atenderSolicitudConEntrega(solicitud.id, {
                items: lineas.map((l) => ({ item_id: Number(l.itemId), cantidad: Number(l.cantidad) })),
                motivo_entrega: motivoEntrega,
                respuesta: respuesta || null,
            });
            toast.success(`Entregado (${actualizada.entrega_codigo})`);
            onActualizada(actualizada);
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'No se pudo registrar la entrega');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal title={`Solicitud ${solicitud.codigo}`} onClose={onCerrar} size="md">
            <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <p className="text-xs text-gray-500">Colaborador</p>
                        <p className="font-medium text-gray-900">{solicitud.empleado_nombre}</p>
                        <p className="text-xs text-gray-400">{solicitud.empleado_documento}{solicitud.empleado_cargo ? ` · ${solicitud.empleado_cargo}` : ''}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Contacto</p>
                        <p className="text-gray-700">{solicitud.telefono || '—'}</p>
                        <p className="text-gray-700 break-all">{solicitud.correo || ''}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Tipo</p>
                        <p className="text-gray-700">{TIPOS[solicitud.tipo] || solicitud.tipo}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Estado</p>
                        <BadgeEstado estado={solicitud.estado} />
                    </div>
                </div>

                {solicitud.detalles.length > 0 && (
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Productos pedidos</p>
                        <ul className="list-disc list-inside text-gray-700">
                            {solicitud.detalles.map((d) => <li key={d.id}>{d.descripcion} × {d.cantidad}</li>)}
                        </ul>
                    </div>
                )}

                {solicitud.detalle && (
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Detalle</p>
                        <p className="text-gray-700 whitespace-pre-line bg-gray-50 rounded-lg p-3">{solicitud.detalle}</p>
                    </div>
                )}

                {abierta && modoEntrega && (
                    <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Entregar del inventario (descuenta stock y genera el Formato EPP)</p>
                        <div className="space-y-2">
                            {lineas.map((linea, idx) => (
                                <div key={idx} className="grid grid-cols-[minmax(0,1fr)_5rem_1rem] items-center gap-2">
                                    <select value={linea.itemId} onChange={(e) => actualizarLinea(idx, 'itemId', e.target.value)} className={campoCls}>
                                        <option value="">Seleccionar producto...</option>
                                        {items.map((i) => (
                                            <option key={i.id} value={i.id}>{i.descripcion}{i.talla ? ` (${i.talla})` : ''} — stock: {i.stock_actual}</option>
                                        ))}
                                    </select>
                                    <input type="number" min="1" value={linea.cantidad} onChange={(e) => actualizarLinea(idx, 'cantidad', e.target.value)} className={campoCls} />
                                    {lineas.length > 1 && (
                                        <button onClick={() => setLineas((prev) => prev.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-600">
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setLineas((prev) => [...prev, { itemId: '', cantidad: 1 }])} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                            <Plus className="h-3.5 w-3.5 mr-1" /> Agregar producto
                        </button>
                        <div className="mt-3">
                            <label className="block text-xs text-gray-500 mb-1">Motivo de la entrega</label>
                            <select value={motivoEntrega} onChange={(e) => setMotivoEntrega(e.target.value)} className={campoCls}>
                                {MOTIVOS_ENTREGA.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-xs text-gray-500 mb-1">Respuesta al colaborador {abierta ? '(la verá al consultar su código)' : ''}</label>
                    <textarea value={respuesta} onChange={(e) => setRespuesta(e.target.value)} rows={2} disabled={!abierta} className={inputCls} />
                </div>

                {solicitud.entrega_codigo && (
                    <button
                        onClick={() => rrhhInventarioService.descargarFormatoEpp(solicitud.entrega_codigo).catch(() => toast.error('No se pudo generar el formato'))}
                        className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                    >
                        <FileDown className="h-4 w-4 mr-1" /> Formato EPP de la entrega {solicitud.entrega_codigo}
                    </button>
                )}
                {!abierta && solicitud.atendido_por && (
                    <p className="text-xs text-gray-400">
                        Cerrada por {solicitud.atendido_por}{solicitud.atendido_en ? ` el ${new Date(solicitud.atendido_en).toLocaleString('es-PE')}` : ''}
                    </p>
                )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 mt-6">
                {abierta && !modoEntrega && (
                    <>
                        <button onClick={() => cambiarEstado('rechazada')} disabled={guardando} className="px-3 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50">
                            Rechazar
                        </button>
                        {solicitud.estado === 'pendiente' && (
                            <button onClick={() => cambiarEstado('en_proceso')} disabled={guardando} className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                                En proceso
                            </button>
                        )}
                        <button onClick={() => setModoEntrega(true)} className="inline-flex items-center px-3 py-2 border border-blue-300 text-blue-700 rounded-lg text-sm hover:bg-blue-50">
                            <PackageCheck className="h-4 w-4 mr-1" /> Entregar del inventario
                        </button>
                        <Button onClick={() => cambiarEstado('atendida')} loading={guardando} disabled={guardando}>
                            Marcar atendida
                        </Button>
                    </>
                )}
                {abierta && modoEntrega && (
                    <>
                        <button onClick={() => setModoEntrega(false)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                            Volver
                        </button>
                        <Button onClick={entregar} loading={guardando} disabled={guardando}>
                            Registrar entrega y cerrar
                        </Button>
                    </>
                )}
                {!abierta && (
                    <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                        Cerrar
                    </button>
                )}
            </div>
        </Modal>
    );
};

// ─── Página principal ───────────────────────────────────────────────────────

const RRHHSolicitudesPage = () => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('abiertas');
    const [seleccionada, setSeleccionada] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const params = filtro === 'abiertas' ? { abiertas: true } : filtro ? { estado: filtro } : {};
            const [sols, its] = await Promise.all([
                rrhhInventarioService.listarSolicitudes(params),
                rrhhInventarioService.listarItems(),
            ]);
            setSolicitudes(sols);
            setItems(its);
        } catch (err) {
            toast.error('Error al cargar las solicitudes');
        } finally {
            setLoading(false);
        }
    }, [filtro]);

    useEffect(() => { cargar(); }, [cargar]);

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="bg-teal-100 p-2.5 rounded-xl">
                        <Inbox className="h-6 w-6 text-teal-700" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">RRHH · Solicitudes</h1>
                        <p className="text-sm text-gray-500">Requerimientos enviados por los colaboradores desde el formulario público</p>
                    </div>
                </div>

                <EnlacePublico />

                <div className="flex items-center space-x-2 mb-4">
                    <select
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        <option value="abiertas">Abiertas (pendientes y en proceso)</option>
                        <option value="pendiente">Pendientes</option>
                        <option value="en_proceso">En proceso</option>
                        <option value="atendida">Atendidas</option>
                        <option value="rechazada">Rechazadas</option>
                        <option value="">Todas</option>
                    </select>
                </div>

                {loading ? (
                    <Loading message="Cargando solicitudes..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Código</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Colaborador</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Tipo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Pedido</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Fecha</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {solicitudes.length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Sin solicitudes</td></tr>
                                )}
                                {solicitudes.map((s) => (
                                    <tr key={s.id} onClick={() => setSeleccionada(s)} className="hover:bg-gray-50 cursor-pointer">
                                        <td className="px-4 py-3 font-mono text-xs text-gray-700">{s.codigo}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">{s.empleado_nombre}</div>
                                            <div className="text-xs text-gray-400">{s.empleado_documento}</div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{TIPOS[s.tipo] || s.tipo}</td>
                                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                                            {s.detalles.length
                                                ? s.detalles.map((d) => `${d.descripcion} ×${d.cantidad}`).join(', ')
                                                : s.detalle}
                                        </td>
                                        <td className="px-4 py-3"><BadgeEstado estado={s.estado} /></td>
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(s.created_at).toLocaleString('es-PE')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {seleccionada && (
                <ModalAtender
                    solicitud={seleccionada}
                    items={items}
                    onCerrar={() => setSeleccionada(null)}
                    onActualizada={() => cargar()}
                />
            )}
        </Layout>
    );
};

export default RRHHSolicitudesPage;
